import { ChangeDetectionStrategy, Component, computed, inject, Injector } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@ortho-next/auth-core';
import { map } from 'rxjs';
import { APP_CONFIG } from '../../core';
import { BaseHelpService, CaseService, Guide, GuideService, UserService } from '../../services';
import { AnatomicalSideLabelPipe, BoneTypeLabelPipe, ConfirmationComponent, HelpComponent, PlanTypeLabelPipe, ReplaceLongLegPipe } from '../../shared';
import { ViewHelpService } from '../services';
import { VwBaseComponent } from '../shared';


/**
 * Horizontal bar on top of view page.
 */
@Component({
	selector: 'vw-navbar',
	templateUrl: './vw-navbar.component.html',
	imports: [
		NgbDropdownModule,
		BoneTypeLabelPipe,
		PlanTypeLabelPipe,
		ReplaceLongLegPipe,
		AnatomicalSideLabelPipe
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class VwNavbarComponent extends VwBaseComponent {

	private readonly config = inject(APP_CONFIG);
	private readonly modalSrv = inject(NgbModal);
	private readonly injector = inject(Injector);
	private readonly userSrv = inject(UserService);
	private readonly caseSrv = inject(CaseService);
	private readonly authSrv = inject(AuthService);
	private readonly guideSrv = inject(GuideService);


	plan = toSignal(this.caseSrv.getCurrentCase());
	isWatermarkVisible = toSignal(this.userSrv.isJPSDemoProduct(), { initialValue: false });
	username = toSignal(this.userSrv.getUserName().pipe(map(name => `${name.firstName} ${name.lastName}`)), { initialValue: '' });
	planStepLabel = computed(() => {
		if (!this.plan()) return '';
		return this.plan()!.isPostOperative ? this.labels().NAVBAR_COMPONENT_PLAN_STEP_POSTOP : this.labels().NAVBAR_COMPONENT_PLAN_STEP_PREOP;
	});

	guideList: Guide[] = [];
	readonly envName = this.config.envName;
	readonly eifuSiteUrl = this.config.eifuSite;
	readonly buildVersion = this.config.buildVer;

	/**
	* Open about page.
	*/
	openAboutPage(): void {
		window.open(`${this.config.aboutSite}${this.langSrv.currentLang}`, '_blank');
	}

	/**
	* Open legal terms page.
	*/
	openLegalTerms(): void {
		this.userSrv.getUserCountry().subscribe(code => {
			window.open(`${this.config.legalTermsSite}?countryCode=${code}`);
		});
	}

	/**
	* Logout.
	*/
	logout(): void {
		this.authSrv.singleSignOut();
	}

	/**
	 * Get Guide list when open dropdown the first time.
	 */
	getGuideList(open: boolean): void {
		if (!!open && (!this.guideList || this.guideList.length === 0)) {
			this.guideSrv.getGuideList().subscribe(list => this.guideList = list);
		}
	}

	/**
	 * Open guide page.
	 */
	openGuide(path: string): void {
		const guideUrl = this.guideSrv.getGuideUrl(path);
		window.open(guideUrl, '_blank');
	}

	/**
	* Open modal of help info.
	*/
	openHelpModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			this.modalSrv.open(HelpComponent, {
				backdropClass: 'wrapper-modal-help', windowClass: 'wrapper-modal-help', injector:
					Injector.create({
						parent: this.injector,
						providers: [{ provide: BaseHelpService, useClass: ViewHelpService }]
					})
			});
		}
	}

	/**
	 * Open confirm modal before exit.
	 */
	openClosingModal(): void {
		if (!this.modalSrv.hasOpenModals()) {
			const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
			(modalRef.componentInstance as ConfirmationComponent).config = {
				title: this.labels().VIEW_NAVBAR_COMPONENT_BACK_PATIENT_MODAL_TITLE,
				message: this.labels().VIEW_NAVBAR_COMPONENT_BACK_PATIENT_MODAL_MSG
			};
			modalRef.result.then(() => this.goToPatientSite()).catch(() => false);
		}
	}

	private goToPatientSite(): void {
		globalThis.location.href = this.config.patplanSite;
	}

}
